import { isNil } from 'lodash-es';
import { type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useIntl } from 'react-intl';

import { CrosshairIcon } from '@dynatrace/strato-icons';

import { INTERACTION_MODE } from '../../../core/components/interactions-overlay/types.js';
import { calculateInitialSelectionAreaPositions } from '../../../core/components/interactions-overlay/utils/calculate-initial-selection-area-positions.js';
import {
  reduceExploreCancelAll,
  reduceExploreSelect,
} from '../../../core/components/interactions-state-helpers/reducers/explore-reducers.js';
import {
  reduceInteractionMode,
  reduceSelectionAreaVariant,
} from '../../../core/components/interactions-state-helpers/reducers/mode-reducers.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import { useToolbarContext } from '../../../core/components/toolbar/hooks/useToolbarContext.js';
import { useA11yToolbarTabIndex } from '../../../core/hooks/useA11yToolbarTabIndex.js';
import {
  useInteractionModeState,
  useSelectState,
} from '../../hooks/store/selectors.js';
import { useSetState } from '../../hooks/store/state-setters.js';
import { useHistogramChartOverlayRef } from '../../hooks/useHistogramChartOverlayRef.js';
import { useHistogramInteractions } from '../../hooks/useHistogramInteractions.js';
import { HISTOGRAM_MESSAGES } from '../../messages/tooltip-text.js';

export const ExploreButton = () => {
  const intl = useIntl();
  const setState = useSetState();
  const tabIndex = useA11yToolbarTabIndex(INTERACTION_MODE.Explore);
  const { pan, zoom } = useHistogramInteractions();
  const overlayRef = useHistogramChartOverlayRef();
  const { updateLastItemSelected } = useToolbarContext();

  const selectState = useSelectState();

  const interactionMode = useInteractionModeState();
  const isSelected = interactionMode === INTERACTION_MODE.Explore;

  const modePlacement = pan?.enabled || zoom?.enabled ? 'menu' : 'auto';

  const createInitialSelectionArea = () => {
    if (!isNil(overlayRef.current)) {
      const bounds = overlayRef.current.getBoundingClientRect();

      const [start, end] = calculateInitialSelectionAreaPositions(bounds);

      setState(
        reduceExploreSelect({
          start,
          end,
          finished: true,
          accessible: true,
          variant: 'select',
          isKeyboardCreated: true,
        }),
      );

      updateLastItemSelected(INTERACTION_MODE.Explore);
    }
  };

  /** Handles the zoom explore when an 'enter' keyboard event happens.
   * @param event - A keyboard event param.
   */
  const handleKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'Enter') {
      createInitialSelectionArea();
    }
  };

  const handleClick = () => {
    if (interactionMode !== 'explore') {
      setState(reduceInteractionMode(INTERACTION_MODE.Explore));
    }

    if (selectState?.start && selectState?.end) {
      if (selectState?.isKeyboardCreated) {
        reduceSelectionAreaVariant('select');
      } else {
        setState(reduceExploreCancelAll);
      }
    }

    updateLastItemSelected(INTERACTION_MODE.Explore);
  };

  return (
    <ChartToolbarRenderer.Control
      prefixIcon={<CrosshairIcon />}
      tooltipText={intl.formatMessage(HISTOGRAM_MESSAGES.zoomModeExplore)}
      tooltipKeyShortcut="E"
      selected={isSelected}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      tabIndex={tabIndex}
      placement={modePlacement}
    />
  );
};
ExploreButton['displayName'] = 'ExploreButton';
